/**
 * Vault-backed integration credential form — shared by Integrations
 * Console and onboarding promo cards.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Copy } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { EntityDrawer } from "../ui";
import { api, type Connection } from "../../lib/api";

export function IntegrationConfigureDrawer({
  conn,
  legalEntityId,
  open,
  onOpenChange,
}: {
  conn: Connection;
  legalEntityId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const fields = [...conn.required_fields, ...conn.optional_fields];
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((f) => [f, ""])),
  );

  const mut = useMutation({
    mutationFn: () =>
      api.upsertConnection({
        legal_entity_id: legalEntityId,
        integration_type: conn.integration_type,
        payload: values,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["connections", legalEntityId] });
      onOpenChange(false);
    },
  });

  const showWebhook = conn.integration_type === "twilio";
  const { data: webhookUrl } = useQuery({
    queryKey: ["twilio-webhook-url", legalEntityId],
    queryFn: () => api.twilioWebhookUrl(legalEntityId),
    enabled: showWebhook && open,
  });

  return (
    <EntityDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={t("app.int_configure_title", { name: conn.label })}
      description={t("app.int_configure_desc")}
      width="lg"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            form={`integration-connect-form-${conn.integration_type}`}
            disabled={mut.isPending}
            className="inline-flex items-center gap-1 rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg hover:opacity-90 disabled:opacity-50"
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            {mut.isPending ? t("app.int_saving") : t("app.int_save")}
          </button>
        </div>
      }
    >
      <form
        id={`integration-connect-form-${conn.integration_type}`}
        onSubmit={(e) => {
          e.preventDefault();
          mut.mutate();
        }}
        className="space-y-4"
      >
        {conn.required_fields.map((f) => (
          <IntegrationFieldInput
            key={f}
            name={f}
            required
            value={values[f]}
            onChange={(v) => setValues({ ...values, [f]: v })}
          />
        ))}
        {conn.optional_fields.map((f) => (
          <IntegrationFieldInput
            key={f}
            name={f}
            required={false}
            value={values[f]}
            onChange={(v) => setValues({ ...values, [f]: v })}
          />
        ))}

        {showWebhook && webhookUrl && (
          <div className="rounded-md border border-border-subtle bg-surface-sunken p-3">
            <p className="text-xs font-medium text-text-primary">
              {t("app.int_twilio_hint")}
            </p>
            <div className="mt-2 flex items-center gap-2">
              <code className="flex-1 break-all rounded bg-surface-default px-2 py-1.5 text-xs text-text-primary">
                {webhookUrl.url}
              </code>
              <button
                type="button"
                onClick={() => navigator.clipboard.writeText(webhookUrl.url)}
                className="rounded border border-border-subtle px-2 py-1.5 text-xs text-text-secondary hover:bg-surface-sunken"
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}

        {mut.isError && (
          <p className="text-sm text-rose-700">
            {(mut.error as Error).message}
          </p>
        )}
      </form>
    </EntityDrawer>
  );
}

function IntegrationFieldInput({
  name,
  value,
  onChange,
  required,
}: {
  name: string;
  value: string;
  onChange: (v: string) => void;
  required: boolean;
}) {
  const label = name
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  const sensitive = /token|secret|key|password|sid/i.test(name);

  return (
    <label className="block">
      <span className="block text-sm font-medium text-text-primary">
        {label}
        {required && <span className="ms-0.5 text-rose-600">*</span>}
      </span>
      <input
        type={sensitive ? "password" : "text"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="mt-1 block w-full rounded-md border border-border-subtle bg-surface-default px-3 py-2 text-sm text-text-primary shadow-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/30"
        autoComplete="off"
      />
    </label>
  );
}
